import { useEffect, useState, useCallback, useRef } from 'react';
import { useSession } from '@clerk/clerk-react';
import { getEnvironmentConfig } from '../environmentConfig';

/** Connection states for the agent WebSocket */
export type ConnectionState = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

/** Message types that the backend can send */
export type BackendMessageType =
    | 'connected'
    | 'error'
    | 'session:started'
    | 'session:paused'
    | 'session:ended'
    | 'agent:thinking'
    | 'agent:toolCall'
    | 'agent:toolResult'
    | 'agent:status'
    | 'run:permissionRequest'
    | 'run:status'
    | 'token:usage'
    | 'debug:snapshot';

export interface AgentWebSocketMessage {
    type: BackendMessageType;
    payload: Record<string, unknown>;
}

export interface UseAgentWebSocketResult {
    /** Current connection state */
    connectionState: ConnectionState;
    /** The connection ID assigned by the server */
    connectionId: string | null;
    /** Send a typed message to the server */
    send: (message: { type: string; payload?: unknown }) => void;
}

const MAX_RECONNECT_DELAY_MS = 30_000;
const BASE_RECONNECT_DELAY_MS = 1_000;

/**
 * Hook for connecting to the agent WebSocket endpoint.
 *
 * Connects only when `enabled` is true. Auto-reconnects with exponential
 * backoff on unexpected disconnects. Forwards all messages to `onMessage`.
 *
 * NOTE: Clerk's `session` object has unstable references, so we store it
 * in a ref to keep `connect` stable and avoid effect re-trigger loops.
 */
export function useAgentWebSocket(
    graphId: string,
    enabled: boolean,
    onMessage?: (message: AgentWebSocketMessage) => void,
): UseAgentWebSocketResult {
    const { session } = useSession();
    const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
    const [connectionId, setConnectionId] = useState<string | null>(null);

    const socketRef = useRef<WebSocket | null>(null);
    const reconnectAttemptRef = useRef(0);
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const wasConnectedRef = useRef(false);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;

    // Clerk session has unstable references — store in ref to avoid
    // recreating `connect` (and re-triggering the effect) every render.
    const sessionRef = useRef(session);
    sessionRef.current = session;

    // Derive a stable boolean for the effect dependency
    const hasSession = !!session;

    // Keep onMessage in a ref so the WebSocket handler always calls the latest version
    const onMessageRef = useRef(onMessage);
    onMessageRef.current = onMessage;

    const cleanup = useCallback(() => {
        if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
            reconnectTimerRef.current = undefined;
        }
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        wasConnectedRef.current = false;
        reconnectAttemptRef.current = 0;
        setConnectionState('disconnected');
        setConnectionId(null);
    }, []);

    const connect = useCallback(async () => {
        const currentSession = sessionRef.current;
        if (!currentSession) return;

        // Clean up any existing socket (but not the reconnect timer — that's managed by the caller)
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        setConnectionState(prev => (prev === 'reconnecting' ? 'reconnecting' : 'connecting'));

        const url = `${getEnvironmentConfig().agentServiceUrl}?graphId=${graphId}`;
        const socket = new WebSocket(url);
        socketRef.current = socket;

        socket.addEventListener('open', async () => {
            try {
                // Read from ref to always get the latest session
                const activeSession = sessionRef.current;
                if (!activeSession) {
                    socket.close();
                    return;
                }
                const token = await activeSession.getToken();
                if (token && socket.readyState === WebSocket.OPEN) {
                    socket.send(JSON.stringify({ type: 'auth', token }));
                }
            } catch (error) {
                console.error('[agent-ws] Failed to get auth token:', error);
                socket.close();
            }
        });

        socket.addEventListener('message', event => {
            try {
                const data: AgentWebSocketMessage = JSON.parse(event.data);

                if (data.type === 'connected') {
                    const payload = data.payload as { connectionId: string };
                    setConnectionState('connected');
                    setConnectionId(payload.connectionId);
                    wasConnectedRef.current = true;
                    reconnectAttemptRef.current = 0;
                }

                // Forward all messages to the consumer
                onMessageRef.current?.(data);
            } catch (error) {
                console.error('[agent-ws] Failed to parse message:', error);
            }
        });

        socket.addEventListener('close', () => {
            const wasConnected = wasConnectedRef.current;
            socketRef.current = null;
            wasConnectedRef.current = false;
            setConnectionId(null);

            // Auto-reconnect if we were previously connected and still enabled
            if (wasConnected && enabledRef.current) {
                const attempt = reconnectAttemptRef.current;
                const delay = Math.min(BASE_RECONNECT_DELAY_MS * Math.pow(2, attempt), MAX_RECONNECT_DELAY_MS);
                reconnectAttemptRef.current = attempt + 1;

                setConnectionState('reconnecting');
                reconnectTimerRef.current = setTimeout(() => {
                    if (enabledRef.current) {
                        connect();
                    }
                }, delay);
            } else {
                setConnectionState('disconnected');
            }
        });

        socket.addEventListener('error', () => {
            // The error event is always followed by a close event — handled there
        });
    }, [graphId]); // session removed — accessed via sessionRef

    const send = useCallback((message: { type: string; payload?: unknown }) => {
        if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify(message));
        }
    }, []);

    // Connect/disconnect based on enabled flag and session availability
    useEffect(() => {
        if (enabled && hasSession) {
            connect();
        } else if (!enabled) {
            cleanup();
        }

        return () => {
            cleanup();
        };
    }, [enabled, hasSession, connect, cleanup]);

    return {
        connectionState,
        connectionId,
        send,
    };
}
